import { Component, OnInit, ViewChildren } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PlaceSearchResult } from '@app/components/pr-form/pr-input-map/pr-input-map.component';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EaFeesFormComponent } from '../ea-fees-form/ea-fees-form.component';
import { EaFeesFormService } from '../ea-fees-form/ea-fees-form.service';
import { EaEstateFormService } from './ea-estate-form.service';

@Component({
  selector: 'ea-estate-form',
  templateUrl: './ea-estate-form.component.html',
  styleUrls: ['./ea-estate-form.component.scss'],
  standalone: false,
})
export class EaEstateFormComponent extends PrFormAbstract implements OnInit {
  users: any[];
  countries: any[] = [];
  personalUseType: any[] = [];
  subscriptionSource: any[] = [];
  realEstateOwnershipTypes: any[] = [
    { id: 'PLEINE_PROPRIETE', value: 'Pleine-propriété' },
    { id: 'NU_PROPRIO', value: 'Nue-propriété' },
    { id: 'USUFRUIT', value: 'Usufruit' },
    { id: 'INDIVISION', value: 'Indivision' },
  ];
  doneSubject: Subject<void> = new Subject<void>();
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);
  toValue: PlaceSearchResult = { address: '' };
  @ViewChildren(EaFeesFormComponent) feesFormsComponents: EaFeesFormComponent;

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private estateDao: EstateDao,
    private referenceDao: ReferenceDao,
    private eaEstateFormService: EaEstateFormService,
    private eaFeesFormService: EaFeesFormService,
  ) {
    super(formConstructor);
  }

  get title() {
    return this.generateTitle(
      'Modifier le bien de jouissance',
      'Ajouter un bien de jouissance',
    );
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getCouple(),
      this.referenceDao.getCountries(),
      this.referenceDao.getPersonalUseType(),
      this.referenceDao.getSubscriptionSource(),
      this.estateDao.getPersonalUses().pipe(catchError(() => of([]))),
    ]).subscribe(
      ([
        users,
        countries,
        personalUseType,
        subscriptionSource,
        existingEstates,
      ]) => {
        this.users = users;
        this.countries = countries;
        this.subscriptionSource = subscriptionSource;

        const estatesList = existingEstates?.data?.list || [];
        const hasExistingResidencePrincipale = estatesList.some(
          (estate: any) =>
            estate.personalUseType === 'RESIDENCE_PRINCIP' &&
            estate.id !== this.entity?.id,
        );

        this.personalUseType = hasExistingResidencePrincipale
          ? personalUseType.filter(
              (type: any) => type.id !== 'RESIDENCE_PRINCIP',
            )
          : personalUseType;

        if (!this.form.get('ownershipType').value)
          this.form
            .get('ownershipType')
            .setValue(this.realEstateOwnershipTypes[0].id);

        this.form
          .get('loans')
          .get('hasLoan')
          .valueChanges.subscribe(hasLoan =>
            hasLoan ? this.patchValueToParentValues() : undefined,
          );
      },
    );
  }

  patchValueToParentValues() {
    let estateForm = this.form.get('realestate')?.value;
    if (!estateForm) return;

    let loanForm = this.form.get('loans');
    loanForm.get('label').setValue(estateForm.label);
    loanForm.get('amountBorrowed').setValue(estateForm.purchasePrice);
    loanForm.get('startDate').setValue(estateForm.startDate);
  }

  onSubmit() {
    this.displayErrors();

    this.selectErrorInput();

    // stop here if form is invalid
    if (this.form.invalid) {
      this.changeTab.next('FIRST');
      return;
    }
    const extract = this.extract();
    let formDTOExtraction = _.merge(this.entity, this.extract());
    delete formDTOExtraction.lastValue;
    if (!extract.loans && formDTOExtraction.loans)
      formDTOExtraction.removeLoan = true;

    this.prFormService.loading();
    this.estateDao
      .postEstate({
        ...formDTOExtraction,
        productType: 'PERSO_USE',
      })
      .subscribe(
        success => {
          this.prFormService.loaded();
          this.doneSubject.next();
          this.notificationService.success('La sauvegarde a réussi');
        },
        errors => {
          console.error(errors);
          this.prFormService.loaded();
          this.notificationService.error('La sauvegarde a échoué');
        },
      );
  }

  public generate = () => this.eaEstateFormService.generate(this.entity);
  public extract = () => this.eaEstateFormService.extract(this.form);
  public submit = () => super.submit();

  get fees() {
    return this.form.get('fees') as UntypedFormArray;
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }
}
